import {
	Button,
	Chip,
	ChipLink,
	Composer,
	ComposerField,
	ComposerRow,
	ComposerSend,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Expandable,
	ExpandableContent,
	ExpandableTrigger,
	Label,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { Plus } from "@ondit/ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown as CollapseChevron, Copy, ExternalLink, FileText, X } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type Block, type PageDetail, type PageEmbed } from "~/core/api/index";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ComposerModelControls, composerModelRequest } from "~/core/ui/composer-model";
import { PageHead, Section, Stat } from "~/core/ui/page";
import { Async, Skeleton } from "~/core/ui/states";
import { problems } from "~/modules/analytics/components/blocks";
import { Drill } from "~/modules/analytics/components/detail";
import { readingPath } from "~/modules/analytics/lib/loop";

const ArticleNotebook = lazy(() => import("~/modules/analytics/components/article-notebook"));

/**
 * The draft, open.
 *
 * Until this screen existed the only thing a customer could do with a generated
 * page was approve it unseen, which asks them to put their name on prose they
 * have not read. Everything here follows from that: the text is editable, the
 * fact table it is allowed to draw on sits beside it, and the sentences the
 * checker could not support are listed with a button that cuts them.
 */
export function EditorPage({ entityId }: { entityId: string; filters?: unknown; setFilters?: unknown }) {
	const { pageId = "" } = useParams();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "page", pageId],
		queryFn: () => api.page(pageId),
		...read,
		// Asked again while a revision is being written, so the text it
		// produced replaces the old one without a reload.
		refetchInterval: (query) =>
			["queued", "running"].includes(query.state.data?.revision_job?.status ?? "") ? 5000 : false,
	});

	return <Async state={state}>{(data) => <Editor entityId={entityId} pageId={pageId} data={data} />}</Async>;
}

function Editor({ entityId, pageId, data }: { entityId: string; pageId: string; data: PageDetail }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
	const put = useMutation({
		mutationFn: (body: Parameters<typeof api.savePage>[1]) => api.savePage(pageId, body),
		onSuccess: refresh,
	});
	const publish = useMutation({
		mutationFn: (revision: number) => api.approvePage(pageId, revision),
		onSuccess: refresh,
	});
	const rewrite = useMutation({
		mutationFn: (ask: Parameters<typeof api.revisePage>[1]) => api.revisePage(pageId, ask),
		onSuccess: refresh,
	});

	const [title, setTitle] = useState(data.page.title);
	const [description, setDescription] = useState(data.page.description);
	const [blocks, setBlocks] = useState<Block[]>(data.page.blocks);
	const [saving, setBusy] = useState(false);
	const revising = ["queued", "running"].includes(data.revision_job?.status ?? "");
	const busy = saving || revising;
	const [dirty, setDirty] = useState(false);
	const [aiOpen, setAiOpen] = useState(false);
	const [feedback, setFeedback] = useState("");

	// A reload after a save brings back what the server stored, which is the
	// version any later edit has to start from.
	useEffect(() => {
		setTitle(data.page.title);
		setDescription(data.page.description);
		setBlocks(data.page.blocks);
		setDirty(false);
	}, [data]);

	const generated = data.recipe !== null;
	const broken = [...problems(blocks, generated, t), ...(!title.trim() ? [t("editor.titleRequired")] : [])];

	async function save() {
		setBusy(true);
		try {
			const result = await put.mutateAsync({ title, description, blocks, revision: data.page.revision });
			toast.success(t("editor.saved", { n: result.revision }));
		} catch (e) {
			toast.error(
				e instanceof ApiError ? (e.code === "bad_request" && e.detail ? e.detail : e.localized(locale)) : String(e),
				{ duration: Infinity },
			);
		} finally {
			setBusy(false);
		}
	}

	async function approve() {
		setBusy(true);
		try {
			const result = await publish.mutateAsync(data.page.revision);
			if (result.status !== "published") {
				toast.info(t("pages.publishPending"));
				return;
			}
			toast.success(t("pages.status.published"));
			navigate(readingPath(`/w/${entityId}`, "published"));
		} catch (e) {
			toast.error(
				e instanceof ApiError ? (e.code === "bad_request" && e.detail ? e.detail : e.localized(locale)) : String(e),
				{ duration: Infinity },
			);
		} finally {
			setBusy(false);
		}
	}

	async function revise() {
		setBusy(true);
		try {
			// The revision rewrites the stored text, so an edit still on screen
			// is saved first rather than asking the person to go and do it.
			if (dirty) await put.mutateAsync({ title, description, blocks, revision: data.page.revision });
			// The model and the thinking budget are the composer's own chips, set
			// once per browser like the Agent's.
			await rewrite.mutateAsync({ feedback, ...composerModelRequest() });
			setAiOpen(false);
			toast.success(t("editor.revisionQueued"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}

	return (
		<>
			<PageHead
				icon={<FileText />}
				title={t("editor.title")}
				lede={data.has_live_version ? t("editor.liveRevisionHint") : undefined}
				actions={
					<div className="row-actions">
						<ChipLink href={readingPath(`/w/${entityId}`, "drafts")} leftIcon={ArrowLeft}>
							{t("editor.back")}
						</ChipLink>
						{dirty ? <span className="hint">{t("editor.unsaved")}</span> : null}
						<Chip
							type="button"
							disabled={busy || !editor || !dirty || broken.length > 0}
							title={whyDisabled(editor, why.editor)}
							onClick={save}
						>
							{saving ? t("editor.saving") : t("editor.save")}
						</Chip>
						{data.status !== "published" ? (
							<Chip
								variant="primary"
								type="button"
								// A draft with an unsaved edit must not be approved: the
								// publish reads the stored revision, so approving here
								// would put the version on screen nowhere and the
								// version on disk everywhere.
								disabled={busy || !editor || dirty || broken.length > 0}
								title={whyDisabled(editor, why.editor)}
								onClick={approve}
							>
								{t("pages.approve")}
							</Chip>
						) : null}
						{data.has_live_version ? (
							<>
								{/* Distribution is the published page's next step, not an
								    afterthought behind an overflow menu. */}
								<ChipLink
									variant={data.status === "published" ? "primary" : undefined}
									href={`/w/${entityId}/publish/distribution?page=${pageId}`}
								>
									{t("nav.distribution")}
								</ChipLink>
								<ChipLink href={data.page.canonical} target="_blank" rel="noopener noreferrer" rightIcon={ExternalLink}>
									{t("editor.preview")}
								</ChipLink>
							</>
						) : null}
					</div>
				}
			/>

			{revising ? (
				<p className="banner" role="status">
					{t("editor.revising")}
				</p>
			) : null}
			{data.revision_job?.status === "failed" ? (
				<div className="banner banner-warn">
					<p>{t("editor.revisionFailed")}</p>
					{data.revision_job.error ? <p className="hint">{data.revision_job.error}</p> : null}
				</div>
			) : null}

			{broken.length > 0 ? (
				<div className="banner banner-warn">
					<ul>
						{broken.map((problem) => (
							<li key={problem}>{problem}</li>
						))}
					</ul>
				</div>
			) : null}

			{/* A fieldset, because "none of this is yours to change" is one
			    attribute here and twenty props otherwise — and because a viewer
			    who can rearrange blocks that will never be saved has been given a
			    scratchpad, not a page. */}
			<div className="notebook-layout">
				<fieldset className="bare-fieldset" disabled={!editor || busy}>
					<div className="editor-main">
						<Suspense fallback={<Skeleton />}>
							<ArticleNotebook
								key={`${pageId}-${data.page.revision}`}
								draft={data.page}
								onAI={generated && editor && !busy ? () => setAiOpen(true) : undefined}
								disabled={!editor || busy}
								onChange={(next) => {
									setDirty(true);
									setTitle(next.title);
									setDescription(next.description);
									setBlocks(next.blocks);
								}}
							/>
						</Suspense>

						{aiOpen ? (
							<div className="writing-composer-dock">
								<Composer>
									<ComposerField
										id="revision-feedback"
										aria-label={t("editor.revisionFeedback")}
										placeholder={t("editor.revisionFeedback")}
										value={feedback}
										maxLength={4000}
										disabled={busy || broken.length > 0}
										// Enter sends, shift+enter breaks the line: the composer is
										// read as a chat box, and a chat box that needs a mouse to
										// send is the one thing nobody expects of one.
										onKeyDown={(e) => {
											if (e.key === "Escape") {
												setAiOpen(false);
												return;
											}
											if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
												e.preventDefault();
												if (feedback.trim() && !busy && broken.length === 0) void revise();
											}
										}}
										onChange={(e) => setFeedback(e.target.value)}
									/>
									<ComposerRow>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Chip
													type="button"
													shape="round"
													leftIcon={Plus}
													disabled={busy || broken.length > 0}
													aria-label={t("writing.actions")}
												/>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="start">
												{["clarify", "shorten", "expand", "outline"].map((action) => (
													<DropdownMenuItem key={action} onSelect={() => setFeedback(t(`writing.${action}Prompt`))}>
														{t(`writing.${action}`)}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
										<ComposerModelControls />
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="composer-row__end"
											aria-label={t("common.close")}
											onClick={() => setAiOpen(false)}
										>
											<X aria-hidden />
										</Button>
										<ComposerSend
											label={t("editor.requestRevision")}
											disabled={!feedback.trim() || busy || broken.length > 0}
											onSubmit={revise}
										/>
									</ComposerRow>
								</Composer>
							</div>
						) : null}
					</div>
				</fieldset>
				<aside className="editor-side notebook-references">
					<div className="stats">
						<Stat label={t("editor.status")} value={t(`pages.status.${data.status}`)} mono={false} />
						<Stat label={t("editor.revisionLabel")} value={String(data.page.revision)} />
						{/* Whether the thing being edited is working. It was on the
						    published list and not here, which is the one screen where
						    somebody is deciding what the page should say. */}
						<Stat
							label={t("editor.citationsLabel")}
							value={
								data.page.cited_this_week > 0 ? (
									<Drill view={{ kind: "citations", label: data.page.title, filters: { page: pageId, range: "7d" } }}>
										{String(data.page.cited_this_week)}
									</Drill>
								) : (
									String(data.page.cited_this_week)
								)
							}
						/>
					</div>

					<Embed pageId={pageId} />

					<Section title={t("editor.facts")} lede={t("editor.factsHint")}>
						{data.facts.length === 0 ? (
							<p className="faint">{t("editor.factsEmpty")}</p>
						) : (
							<div className="evidence-list">
								{data.facts.map((fact) => (
									<Expandable
										className="evidence-item"
										defaultOpen={!editor}
										key={`${fact.entity}-${fact.key}-${editor}`}
									>
										<ExpandableTrigger asChild>
											<Button
												variant="ghost"
												type="button"
												className="group w-full h-auto min-h-(--control-h) justify-start whitespace-normal text-left"
											>
												{pick(locale, [fact.label_en, fact.label_zh]) || fact.key}
												<span className="faint"> · {fact.entity}</span>
												<CollapseChevron
													aria-hidden
													className="ml-auto shrink-0 transition-transform group-data-[state=open]:rotate-180"
												/>
											</Button>
										</ExpandableTrigger>
										<ExpandableContent>
											<p>
												{typeof fact.value === "object" ? JSON.stringify(fact.value) : String(fact.value)}
												{fact.unit ? ` ${fact.unit}` : ""}
											</p>
											{fact.source_url ? (
												<a href={fact.source_url} target="_blank" rel="noopener noreferrer">
													{t("settings.source")} <ExternalLink aria-hidden />
												</a>
											) : null}
										</ExpandableContent>
									</Expandable>
								))}
							</div>
						)}
					</Section>

					{data.reflow ? (
						<Section title={t("editor.reflow")}>
							<div className="panel stack-tight">
								<span className="pane-value">
									{data.reflow.before === null ? "—" : fmt.percent(locale, data.reflow.before)} →{" "}
									{data.reflow.after === null ? "—" : fmt.percent(locale, data.reflow.after)}
								</span>
								<span className="faint">
									{data.reflow.significant ? t("reflow.significant") : t("reflow.notSignificant")}
								</span>
							</div>
						</Section>
					) : null}

					<Section title={t("editor.revisions")}>
						<div className="table-scroll">
							<Table variant="resource">
								<TableBody>
									{data.revisions.map((revision) => (
										<TableRow key={revision.revision}>
											<TableCell className="num mono">{revision.revision}</TableCell>
											<TableCell className="wrap faint">
												{revision.note === "pending review"
													? t("pages.status.review")
													: revision.note === "superseded on approval"
														? t("editor.archivedBeforeApproval")
														: revision.note}
											</TableCell>
											<TableCell className="mono">{fmt.relative(locale, revision.created_at)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</Section>
				</aside>
			</div>
		</>
	);
}

/**
 * The page as something to paste into the brand's own site.
 *
 * ondit keeps publishing at its own URL — a listing is worth citing because the
 * brand does not own it — and the brand's site still has to answer the same
 * questions. The fragment carries the citation in both halves, visibly and in
 * the JSON-LD, because the same paragraphs on two domains with nothing linking
 * them is how both end up looking like the copy.
 *
 * Loaded on demand rather than with the page: most visits to this screen are
 * somebody editing, and the fragment is only interesting once they have
 * stopped.
 */
function Embed({ pageId }: { pageId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const [embed, setEmbed] = useState<PageEmbed | null>(null);
	const [busy, setBusy] = useState(false);

	async function load() {
		setBusy(true);
		try {
			setEmbed(await api.pageEmbed(pageId));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function copy(text: string) {
		try {
			await navigator.clipboard.writeText(text);
			toast.success(t("embed.copied"));
		} catch {
			// A browser that refuses the clipboard — an insecure origin, a
			// permission denied — leaves the textarea, which is selectable.
			toast.error(t("embed.copyFailed"));
		}
	}

	return (
		<Section title={t("embed.title")} lede={t("embed.hint")}>
			{embed === null ? (
				<Button disabled={busy} onClick={load} variant="subtle">
					{t("embed.show")}
				</Button>
			) : (
				<div className="stack">
					{/* A draft can be copied — somebody may want it live on their own
					    site first — but the fragment cites a URL that does not answer
					    yet, and saying so beats a broken link on a customer's page. */}
					{embed.published ? null : <p className="hint">{t("embed.draftWarning")}</p>}
					<Field label={t("embed.html")} onCopy={() => copy(embed.html)} value={embed.html} />
					{embed.jsonld ? (
						<Field label={t("embed.jsonld")} onCopy={() => copy(embed.jsonld)} value={embed.jsonld} />
					) : (
						<p className="faint">{t("embed.noFaq")}</p>
					)}
				</div>
			)}
		</Section>
	);
}

function Field({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
	const t = useT();
	return (
		<div className="field wide">
			<div className="row">
				<Label htmlFor={`embed-${label}`}>{label}</Label>
				<Button aria-label={t("embed.copy")} onClick={onCopy} size="icon" variant="ghost">
					<Copy aria-hidden />
				</Button>
			</div>
			<Textarea id={`embed-${label}`} readOnly rows={8} value={value} />
		</div>
	);
}
